/**
 * Role Detection Algorithm
 *
 * Classifies page elements into semantic roles (heading, subheading, body,
 * hero-image, supporting-media, decoration) using size, position, and
 * font size heuristics. These roles are used by animation presets to assign
 * coordinated animations.
 */

import {
  DetectedRole,
  ElementRole,
  PageContext,
  RoleDetectionElement,
  ScoredElement,
  StaggerDirection
} from "./types";

/** Element types that should never be animated by presets */
const EXCLUDED_TYPES = new Set([
  "background",
  "caption",
  "audio",
  "table",
  "table2",
  "grid"
]);

/** Text-based element types */
const TEXT_TYPES = new Set(["textbox"]);

/** Media element types */
const MEDIA_TYPES = new Set(["image", "video"]);

/** Decoration-eligible element types */
const DECORATION_TYPES = new Set(["vector", "qrcode", "line"]);

/**
 * Calculate area of an element.
 */
const calculateArea = (element: RoleDetectionElement): number =>
  Math.abs(element.width * element.height);

/**
 * Calculate a score for textbox elements based on font size and area.
 * Higher scores indicate more visually prominent text elements (likely headings).
 */
export const calculateTextboxScore = (
  element: RoleDetectionElement
): number => {
  const fontSize = element.fontSize ?? 0;
  const area = calculateArea(element);
  return fontSize * area;
};

/**
 * Calculate a score for media elements based on area.
 * Larger elements are ranked higher (likely hero images).
 */
export const calculateMediaScore = (element: RoleDetectionElement): number =>
  calculateArea(element);

/**
 * Sort elements by their computed score in descending order.
 */
export const sortElementsByScore = (
  elements: RoleDetectionElement[],
  scoreFn: (element: RoleDetectionElement) => number
): ScoredElement[] =>
  elements
    .map(element => ({ element, score: scoreFn(element) }))
    .sort((a, b) => b.score - a.score);

/**
 * Check if an element should be excluded from preset animation.
 */
const isExcluded = (element: RoleDetectionElement): boolean =>
  EXCLUDED_TYPES.has(element.type);

/**
 * Check if an element is a group with mixed content types (text AND media).
 * Mixed groups should be unwrapped so children are classified individually.
 */
export const shouldUnwrapGroup = (
  group: RoleDetectionElement,
  allElements: Record<string, RoleDetectionElement>
): boolean => {
  if (group.type !== "group" || !group.elementsOrder?.length) return false;

  const childTypes = new Set<string>();
  for (const childId of group.elementsOrder) {
    const child = allElements[childId];
    if (child) childTypes.add(child.type);
  }

  const hasText = childTypes.has("textbox");
  const hasMedia =
    childTypes.has("image") ||
    childTypes.has("video") ||
    childTypes.has("vector");

  return hasText && hasMedia;
};

/**
 * Get the classifiable elements from a page, handling group unwrapping.
 * Groups with mixed content are unwrapped; homogeneous groups are kept as units.
 */
export const getClassifiableElements = (
  elements: RoleDetectionElement[],
  allElements: Record<string, RoleDetectionElement>
): RoleDetectionElement[] => {
  const result: RoleDetectionElement[] = [];

  for (const element of elements) {
    if (isExcluded(element)) continue;

    if (element.type === "group") {
      if (shouldUnwrapGroup(element, allElements)) {
        // Unwrap: add children instead of the group
        for (const childId of element.elementsOrder ?? []) {
          const child = allElements[childId];
          if (child && !isExcluded(child)) {
            result.push(child);
          }
        }
      } else if (element.elementsOrder?.length) {
        // Homogeneous group: animate as a single unit, classify by first child's type
        result.push(element);
      }
    } else {
      result.push(element);
    }
  }

  return result;
};

/**
 * Get the effective type for an element, handling groups.
 * For groups, recursively resolves through nested groups to find
 * the type of the first leaf (non-group) child element.
 */
const getEffectiveType = (
  element: RoleDetectionElement,
  allElements: Record<string, RoleDetectionElement>
): string => {
  if (element.type === "group" && element.elementsOrder?.length) {
    const firstChildId = element.elementsOrder[0];
    if (firstChildId !== undefined) {
      const firstChild = allElements[firstChildId];
      if (firstChild) {
        return getEffectiveType(firstChild, allElements);
      }
    }
  }
  return element.type;
};

/**
 * Assign a role to a single element based on its type and rank among peers.
 */
export const detectElementRole = (
  _element: RoleDetectionElement,
  rank: number,
  totalInCategory: number,
  category: "text" | "media" | "decoration"
): ElementRole => {
  if (category === "text") {
    if (rank === 0) return "heading";
    if (rank === 1) return "subheading";
    return "body";
  }

  if (category === "media") {
    if (rank === 0) return "hero-image";
    if (rank < totalInCategory - 1 || totalInCategory <= 2)
      return "supporting-media";
    return "decoration";
  }

  // decoration category
  return "decoration";
};

/**
 * Detect semantic roles for all elements on a page.
 *
 * Algorithm:
 * 1. Filter out excluded elements and handle group unwrapping
 * 2. Separate elements into text, media, and decoration categories
 * 3. Score and rank elements within each category
 * 4. Assign roles based on rank
 */
export const detectAllRoles = (
  pageElements: RoleDetectionElement[],
  allElements: Record<string, RoleDetectionElement>,
  _pageContext?: PageContext
): DetectedRole[] => {
  const classifiable = getClassifiableElements(pageElements, allElements);

  if (classifiable.length === 0) return [];

  const textElements: RoleDetectionElement[] = [];
  const mediaElements: RoleDetectionElement[] = [];
  const decorationElements: RoleDetectionElement[] = [];

  for (const element of classifiable) {
    const effectiveType = getEffectiveType(element, allElements);

    if (TEXT_TYPES.has(effectiveType)) {
      textElements.push(element);
    } else if (MEDIA_TYPES.has(effectiveType)) {
      mediaElements.push(element);
    } else if (DECORATION_TYPES.has(effectiveType)) {
      decorationElements.push(element);
    }
  }

  const roles: DetectedRole[] = [];

  // Score and rank text elements
  const scoredText = sortElementsByScore(textElements, calculateTextboxScore);
  for (const [i, scored] of scoredText.entries()) {
    roles.push({
      uniqueId: scored.element.uniqueId,
      role: detectElementRole(scored.element, i, scoredText.length, "text"),
      score: scored.score
    });
  }

  // Score and rank media elements
  const scoredMedia = sortElementsByScore(mediaElements, calculateMediaScore);
  for (const [i, scored] of scoredMedia.entries()) {
    roles.push({
      uniqueId: scored.element.uniqueId,
      role: detectElementRole(scored.element, i, scoredMedia.length, "media"),
      score: scored.score
    });
  }

  // Score and rank decoration elements
  const scoredDecoration = sortElementsByScore(
    decorationElements,
    calculateMediaScore
  );
  for (const [i, scored] of scoredDecoration.entries()) {
    roles.push({
      uniqueId: scored.element.uniqueId,
      role: detectElementRole(
        scored.element,
        i,
        scoredDecoration.length,
        "decoration"
      ),
      score: scored.score
    });
  }

  return roles;
};

/**
 * Sort elements for staggered animation based on the specified direction.
 *
 * - `ttb-ltr`: Top-to-bottom, left-to-right (reading order)
 * - `center-out`: From center of page outward
 * - `random`: Random order (seeded by element IDs for consistency)
 */
export const sortByStaggerDirection = (
  elements: RoleDetectionElement[],
  direction: StaggerDirection,
  pageContext?: PageContext
): RoleDetectionElement[] => {
  const sorted = [...elements];

  switch (direction) {
    case "ttb-ltr":
      sorted.sort((a, b) => {
        const rowDiff = a.top - b.top;
        // Consider elements within 20px to be on the same "row"
        if (Math.abs(rowDiff) > 20) return rowDiff;
        return a.left - b.left;
      });
      break;

    case "center-out": {
      const centerX = pageContext
        ? pageContext.pageWidth / 2
        : Math.max(...elements.map(e => e.left + e.width / 2));
      const centerY = pageContext
        ? pageContext.pageHeight / 2
        : Math.max(...elements.map(e => e.top + e.height / 2));

      sorted.sort((a, b) => {
        const distA = Math.hypot(
          a.left + a.width / 2 - centerX,
          a.top + a.height / 2 - centerY
        );
        const distB = Math.hypot(
          b.left + b.width / 2 - centerX,
          b.top + b.height / 2 - centerY
        );
        return distA - distB;
      });
      break;
    }

    case "random": {
      // Deterministic shuffle based on element IDs for consistency
      const hashCode = (str: string): number => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash |= 0; // Convert to 32bit integer
        }
        return hash;
      };

      sorted.sort((a, b) => hashCode(a.uniqueId) - hashCode(b.uniqueId));
      break;
    }
  }

  return sorted;
};
